/* Rico Starr — the rules behind the bag and the price display (The Gaygency, 2026-10).
   Pure functions, no page access, so they can be unit tested (tests/bag-logic.test.js, `bun test`).
   The page code that draws things is in snippets/rs-cart-drawer.liquid. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.RSBagLogic = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  // Things that sit in a bag but are not shirts.
  var NOT_SHIRTS = ['gift-card', 'custom-shirt-set-up-fee', 'custom-shirt-design-fee', 'custom-set-up-design-fee'];

  // Prices follow the shopper's currency (Daniel, 2026-10-06): the page sets it once with setCurrency()
  // from Shopify's active currency, and cart.js amounts are already in that currency, in hundredths.
  // setCurrency(code, sample): `sample` is Liquid's {{ 123456789 | money }} for the active currency, so the bag
  // copies the store's own symbol, separators and decimals exactly ("€1.234.567,89" -> "€72,95").
  var CURRENCY = 'USD', FMT = null;
  function parseSample(sample) {
    var m = sample && String(sample).match(/^(.*?)1(\D?)234(\D?)567(?:(\D)89|(\D?)568)?(.*)$/);
    if (!m) return null;
    return { pre: m[1], group: m[2], dec: m[4] || null, post: m[6] };
  }
  function setCurrency(code, sample) { CURRENCY = code || 'USD'; FMT = code ? parseSample(sample) : null; }
  function money(cents, code) {
    var cur = code || CURRENCY || 'USD';
    if (FMT && !code) {
      var units = FMT.dec ? Math.round(cents) / 100 : Math.round(cents / 100);
      var parts = (FMT.dec ? units.toFixed(2) : String(units)).split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, FMT.group);
      return FMT.pre + whole + (FMT.dec ? FMT.dec + parts[1] : '') + FMT.post;
    }
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur }).format(Math.round(cents) / 100);
    } catch (e) {
      return (Math.round(cents) / 100).toFixed(2) + ' ' + cur;
    }
  }

  // Price after a percent-off code. Shopify drops the fraction of a cent on the discount (5499 at 15% is
  // 824 off, not 825), so we do the same and the page matches the bag to the cent.
  function discounted(cents, percent) {
    var p = Number(percent);
    if (!p || p <= 0 || p >= 100) return cents;
    return cents - Math.floor(cents * p / 100);
  }

  function shirtCount(cart) {
    return (cart.items || []).reduce(function (n, i) {
      if (i.gift_card || NOT_SHIRTS.indexOf(i.handle) !== -1) return n;
      return n + (i.quantity || 0);
    }, 0);
  }

  function hasCode(cart) {
    return (cart.cart_level_discount_applications || []).some(function (d) { return d.type === 'discount_code'; });
  }

  // Progress toward "buy N shirts, get X% off". Hidden when a code is on the bag: Eric's discounts do not
  // combine, so promising a second discount there would be untrue.
  function progress(cart, rule) {
    var t = Number(rule && rule.threshold) || 0;
    var pct = Number(rule && rule.percent) || 0;
    var count = shirtCount(cart);
    var remaining = Math.max(0, t - count);
    var reached = t > 0 && count >= t;
    var message = reached
      ? 'You unlocked ' + pct + '% off'
      : remaining === 1
        ? '1 more shirt gets you ' + pct + '% off'
        : 'Add ' + remaining + ' more shirts to get ' + pct + '% off';
    return {
      count: count, remaining: remaining, reached: reached,
      percentFull: t > 0 ? Math.min(100, Math.round(count / t * 100)) : 0,
      message: message,
      show: t > 0 && pct > 0 && count > 0 && !hasCode(cart)
    };
  }

  // Add more, save more (Daniel, 2026-10-06). tiers = [[shirts, percent], ...] ascending; only shirts whose
  // unit price is at least minCents (US cents, times Shopify.currency.rate) count. The discounts themselves are
  // Shopify automatic discounts; this only explains them.
  function tierProgress(cart, rule) {
    var tiers = (rule && rule.tiers) || [];
    var min = Math.round((Number(rule && rule.minCents) || 0) * (Number(rule && rule.rate) || 1));
    var count = (cart.items || []).reduce(function (n, i) {
      if (i.gift_card || NOT_SHIRTS.indexOf(i.handle) !== -1) return n;
      var unit = i.original_price || i.price || 0;
      return unit >= min ? n + (i.quantity || 0) : n;
    }, 0);
    var cur = null, next = null;
    tiers.forEach(function (t) { if (count >= t[0]) cur = t; else if (!next) next = t; });
    var top = tiers.length ? tiers[tiers.length - 1] : null;
    var shirts = function (k) { return k === 1 ? '1 more shirt' : k + ' more shirts'; };
    var message = !next ? (cur ? 'You unlocked ' + cur[1] + '% off' : '')
      : (cur ? "You're saving " + cur[1] + '%. ' : '') + 'Add ' + shirts(next[0] - count) + ' to save ' + next[1] + '%';
    return {
      count: count, percent: cur ? cur[1] : 0, reached: !next && !!cur,
      percentFull: top ? Math.min(100, Math.round(count / top[0] * 100)) : 0,
      message: message,
      show: tiers.length > 0 && (cart.item_count || 0) > 0 && !hasCode(cart)
    };
  }

  // Free shipping bar (Daniel, 2026-10-06). The threshold is set in US cents; rate is Shopify.currency.rate,
  // so a shopper in another currency sees the same threshold in their own money.
  function shipProgress(cart, rule) {
    var base = Number(rule && rule.thresholdCents) || 0;
    var rate = Number(rule && rule.rate) || 1;
    var t = Math.round(base * rate);
    var total = cart.total_price || 0;
    var remaining = Math.max(0, t - total);
    var reached = t > 0 && total >= t;
    return {
      remainingCents: remaining, reached: reached,
      percentFull: t > 0 ? Math.min(100, Math.round(total / t * 100)) : 0,
      message: reached ? 'You unlocked free shipping' : "You're " + money(remaining) + ' away from free shipping',
      show: t > 0 && (cart.item_count || 0) > 0
    };
  }

  function savings(cart) {
    var cents = cart.total_discount || 0;
    if (!cents) {
      cents = (cart.items || []).reduce(function (n, i) { return n + Math.max(0, (i.original_line_price || 0) - (i.final_line_price || 0)); }, 0);
    }
    if (cents <= 0) return { show: false, cents: 0, label: '', amount: '' };
    var apps = cart.cart_level_discount_applications || [];
    var title = apps.length && apps[0].title ? apps[0].title : '';
    return { show: true, cents: cents, label: title ? title + ' applied' : 'Discount applied', amount: 'You save ' + money(cents) };
  }

  // The share of what the shopper pays that goes to charity. percent comes from a theme setting.
  function giving(cart, percent) {
    var p = Number(percent) || 0;
    var total = cart.total_price || 0;
    if (p <= 0 || total <= 0) return { show: false, cents: 0, amount: '', percent: p };
    var cents = Math.round(total * p / 100);
    return { show: true, cents: cents, amount: money(cents), percent: p };
  }

  // Recommended products minus what is already in the bag and what is sold out.
  function suggest(recs, cart, limit) {
    var inBag = (cart.items || []).map(function (i) { return i.handle; });
    return (recs || []).filter(function (p) {
      return p.available !== false && inBag.indexOf(p.handle) === -1 && NOT_SHIRTS.indexOf(p.handle) === -1;
    }).slice(0, limit || 2);
  }

  return { money: money,
    setCurrency: setCurrency, discounted: discounted, shirtCount: shirtCount, hasCode: hasCode, progress: progress, shipProgress: shipProgress, tierProgress: tierProgress, savings: savings, giving: giving, suggest: suggest };
});
